import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef } from 'react'

import { useToastStack, type ToastStackApi } from '@/components/feedback/toast-stack'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { actionToast, isOpenAction, istanbulToIso, isTimeOfDay, normalizeTime } from '@/lib/hr'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import {
  hrApi,
  type HrAction,
  type HrRecordKind,
  type HrRequestCheck,
  type HrRequestCheckRead,
  type HrRequestDetail,
} from '@/services/http/hr'
import type { MovementValue } from '@/features/hr/request-value'

const t = messages.hr

export function actionLabel(type: string): string {
  return t.actions.label[type] ?? type
}

/**
 * Görünen satırların son aksiyonları. Açık (sırada/çalışan) aksiyon varken birkaç saniyede bir
 * yenilenir; biri kapanınca kaydın kendisi de değişmiştir, Cube listesi tazelenir.
 */
export function useLatestActions(kind: HrRecordKind, ids: string[], cubeView: string) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: queryKeys.hr.actions(kind, ids),
    queryFn: () => hrApi.latestActions(kind, ids),
    enabled: ids.length > 0,
    refetchInterval: (state) => (state.state.data?.some(isOpenAction) ? 4000 : false),
  })
  const openIds = (query.data ?? []).filter(isOpenAction).map((a) => a.id)
  const previousOpen = useRef<string[]>([])
  const openKey = openIds.join()
  useEffect(() => {
    const closed = previousOpen.current.some((id) => !openIds.includes(id))
    previousOpen.current = openIds
    if (closed) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cube(cubeView) })
      void queryClient.invalidateQueries({ queryKey: ['hr', 'history', kind] })
    }
    // `openIds` her render yeni dizi; `openKey` içeriği temsil eder.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey, cubeView, kind, queryClient])
  // Kimlik veriyle birlikte değişsin: sütunlar buna bağlı, her render yeni Map tabloyu baştan kurar.
  return useMemo(
    () => new Map((query.data ?? []).map((action) => [action.target_id, action])),
    [query.data],
  )
}

/** Menüdeki açık iş sayısı (İşlerim ile aynı sorgu). Kalem yalnız yetkiliye görünür. */
export function useOpenTaskCount(): number | undefined {
  const list = useQuery({
    queryKey: queryKeys.hr.tasks(),
    queryFn: hrApi.tasks,
    refetchInterval: 5 * 60_000,
  })
  return list.data?.length
}

/** Onaylar sekme sayıları ve menü rozetleri; bildirim olayı (SSE) gelince tazelenir. */
export function useRequestCounts() {
  const query = useQuery({
    queryKey: queryKeys.hr.requestCounts(),
    queryFn: hrApi.requestCounts,
    refetchInterval: 2 * 60_000,
  })
  return query.data
}

const TRACK_POLL_MS = 3000
/** Bundan uzun açık kalan aksiyonun izlenmesi bırakılır; durum satırda ve geçmişte kalır. */
const TRACK_LIMIT_MS = 15 * 60_000

/**
 * Aksiyon kapanana dek bildirimini günceller (sıraya alındı → devam ediyor → sonuç). Bileşenden
 * bağımsız döner: satır kapansa ya da sayfa değişse de izleme sürer. Ara adımlar yalnız açık kartı
 * günceller (kullanıcı kapattıysa geri gelmez); sonuç kapatılmış olsa da yeniden gösterilir.
 */
function trackAction(action: HrAction, api: ToastStackApi, onClosed: () => void): void {
  const label = actionLabel(action.type)
  const startedAt = Date.now()
  let last = action
  let closed = false
  /** Sıradayken kart "Kuyruktan çıkar" sunar; akış aldıktan sonra düğme kalkar. */
  const withCancel = (current: HrAction) => ({
    ...actionToast(current, label),
    action:
      current.status === 'bekliyor'
        ? {
            label: t.actions.cancelQueued,
            onClick: () => {
              hrApi.cancelAction(current.id).then(
                (cancelled) => {
                  closed = true
                  api.toast(actionToast(cancelled, label))
                  onClosed()
                },
                (error: unknown) =>
                  api.toast({
                    type: 'error',
                    title: t.actions.cancelQueuedFailed,
                    description: toProblem(error).detail ?? undefined,
                  }),
              )
            },
          }
        : undefined,
  })
  const id = api.toast(withCancel(action))
  if (!isOpenAction(action)) return onClosed()
  /** İzleme bırakılırken dönen simge kalmasın: kart bilgi notuna döner. */
  const stopWatching = () =>
    api.update(id, {
      ...actionToast(last, label),
      type: 'info',
      description: t.actions.progress.timeout,
    })
  const tick = async () => {
    if (closed) return
    if (Date.now() - startedAt > TRACK_LIMIT_MS) return stopWatching()
    try {
      const latest = await hrApi.latestActions(action.target_kind as HrRecordKind, [
        action.target_id,
      ])
      const current = latest.find((item) => item.id === action.id)
      // Aynı kayda daha yeni bir aksiyon verildiyse onu kendi izleyicisi takip eder.
      if (!current) return stopWatching()
      last = current
      if (closed) return
      if (!isOpenAction(current)) {
        api.toast(actionToast(current, label))
        return onClosed()
      }
      api.update(id, withCancel(current))
    } catch {
      // Geçici ağ hatası: bir sonraki turda yeniden sorulur.
    }
    window.setTimeout(() => void tick(), TRACK_POLL_MS)
  }
  window.setTimeout(() => void tick(), TRACK_POLL_MS)
}

/**
 * Talep değişince etkilenen her görünüm: talep listeleri ve sayıları, talebin ve bağlı kaydın
 * (hareket/izin) geçmişi, satır rozetleri, Müdahaleler ve İşlerim.
 */
export function refreshRequestViews(queryClient: QueryClient, detail: HrRequestDetail): void {
  const keys: readonly (readonly unknown[])[] = [
    ['hr', 'requests'],
    queryKeys.hr.history('talep', detail.id),
    queryKeys.hr.interventions(),
    queryKeys.hr.tasks(),
    ['hr', 'actions'],
  ]
  for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey })
  if (detail.leave_id) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.hr.history('izin', detail.leave_id) })
  }
  if (detail.hareket_id) {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.hr.history('hareket', detail.hareket_id),
    })
  }
}

/**
 * Onaylanan talebin hedef sistem (DİA/Passgage) sonucunu bildirimde izler: sıraya alındı →
 * devam ediyor → sonuç. Talep ayrıntısı yoklanır; sonuçta ilgili görünümler tazelenir.
 */
export function trackRequestResult(
  detail: HrRequestDetail,
  api: ToastStackApi,
  onClosed: (latest: HrRequestDetail) => void,
): void {
  const action = detail.action
  if (!action) return
  const label = actionLabel(action.type)
  const startedAt = Date.now()
  const id = api.toast(actionToast(action, label))
  if (!isOpenAction(action)) return onClosed(detail)
  const tick = async () => {
    if (Date.now() - startedAt > TRACK_LIMIT_MS) {
      return api.update(id, { type: 'info', description: t.actions.progress.timeout })
    }
    try {
      const latest = await hrApi.request(detail.id)
      const current = latest.action
      // Bu arada yeniden denendiyse yeni aksiyonu kendi izleyicisi takip eder.
      if (!current || current.id !== action.id) return onClosed(latest)
      if (!isOpenAction(current)) {
        api.toast(actionToast(current, label))
        return onClosed(latest)
      }
      api.update(id, actionToast(current, label))
    } catch {
      // Geçici ağ hatası: bir sonraki turda yeniden sorulur.
    }
    window.setTimeout(() => void tick(), TRACK_POLL_MS)
  }
  window.setTimeout(() => void tick(), TRACK_POLL_MS)
}

/** Kuyrukta bekleyen aksiyonu geri alır (satır menüsünden). */
export function useCancelQueuedAction(kind: HrRecordKind, id: string) {
  const queryClient = useQueryClient()
  const { toast } = useToastStack()
  return useMutation({
    mutationFn: (actionId: string) => hrApi.cancelAction(actionId),
    onSuccess: (action) => {
      toast(actionToast(action, actionLabel(action.type)))
      void queryClient.invalidateQueries({ queryKey: ['hr', 'actions', kind] })
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.history(kind, id) })
    },
    onError: (error) =>
      toast({
        type: 'error',
        title: t.actions.cancelQueuedFailed,
        description: toProblem(error).detail ?? undefined,
      }),
  })
}

/** Aksiyonu kuyruğa koyar; ilerleme bildirimi ve önbellek tazeleme burada. */
export function useRunAction(kind: HrRecordKind, id: string) {
  const queryClient = useQueryClient()
  const toastApi = useToastStack()
  return useMutation({
    mutationFn: ({ type, params }: { type: string; params?: Record<string, unknown> }) =>
      hrApi.createAction({ type, target_kind: kind, target_id: id, params: params ?? {} }),
    onSuccess: (action) => {
      const refresh = () => {
        void queryClient.invalidateQueries({ queryKey: ['hr', 'actions', kind] })
        void queryClient.invalidateQueries({ queryKey: queryKeys.hr.history(kind, id) })
        void queryClient.invalidateQueries({ queryKey: queryKeys.hr.tasks() })
      }
      refresh()
      trackAction(action, toastApi, refresh)
    },
    onError: (error) => {
      toastApi.toast({
        type: 'error',
        title: t.actions.failed,
        description: toProblem(error).detail ?? undefined,
      })
    },
  })
}

/**
 * Giriş/çıkış talebinin ön kontrolü (sunucu): gelecek / başlangıç öncesi zaman engellenir, aynı
 * gün onaylı izin ve ±5 dk Passgage hareketi uyarılır. Değer durulunca sorulur.
 */
export function useMovementCheck(
  sicilno: string | null | undefined,
  value: MovementValue,
  hareketId?: string | null,
): HrRequestCheckRead | null {
  const time = normalizeTime(value.time)
  const ready = !!sicilno && !!value.date && isTimeOfDay(time)
  const body = useDebouncedValue(
    ready
      ? {
          sicilno: sicilno ?? '',
          giris_cikis: value.girisCikis,
          occurred_at: istanbulToIso(value.date, time),
          hareket_id: hareketId ?? null,
        }
      : null,
    350,
  )
  const query = useQuery({
    queryKey: ['hr', 'request-check', body],
    queryFn: () => hrApi.checkRequest(body as HrRequestCheck),
    enabled: body !== null,
    staleTime: 30_000,
  })
  return ready ? (query.data ?? null) : null
}
